import { db, Timestamp } from '@/firebase.js'
import { firestoreAction } from 'vuexfire'
import { DateTime } from 'luxon'
import { newId } from '@/utils'

const getDefaultState = () => {
  return {
    data: [],
    ref: ''
  }
}

const state = getDefaultState()

const getters = {
  data(state) {
    return { ...state.data }
  },
  ref(state) {
    return state.ref
  },
  transfers(state) {
    return Object.assign({}, ...state.data.map((i) => ({ [i.id]: i })))
  },
  transferById(state) {
    return (id) => state.data.find((t) => t.id === id) || null
  },
  statusById(state) {
    return (id) => state.data.find((t) => t.id === id).status || null
  },
  transfersByStoreId(state, getters) {
    return (storeId) => {
      return Object.values(getters.transfers).filter(
        (t) =>
          t.sourceStoreId === storeId ||
          t.destinationStoreId === storeId ||
          t.sourceStoreId === '' ||
          t.sourceStoreId === null
      )
    }
  },
  isTransfer(state, getters) {
    return getters.transfers && Object.values(getters.transfers).length > 0
  },
  requestedTransfers(state, getters) {
    return getters.transfersOrdered
      .filter((t) => t.status === 'requested')
      .map((t) => t.id)
  },
  approvedTransfers(state, getters) {
    return getters.transfersOrdered
      .filter((t) => t.status === 'approved')
      .map((t) => t.id)
  },
  inTransitTransfers(state, getters) {
    return getters.transfersOrdered
      .filter((t) => t.status === 'inTransit')
      .map((t) => t.id)
  },
  completedTransfers(state, getters) {
    return getters.transfersOrdered
      .filter((t) => t.status === 'completed')
      .map((t) => t.id)
  },
  rejectedTransfers(state, getters) {
    return getters.transfersOrdered
      .filter((t) => t.status === 'rejected')
      .map((t) => t.id)
  },
  pendingTransfers(state, getters) {
    const pending = getters.transfersOrdered.filter(
      (t) =>
        t.status === 'requested' ||
        t.status === 'approved' ||
        t.status === 'inTransit'
    )
    console.log('Pending transfers:', pending)
    return pending.map((t) => t.id)
  },
  pendingTransfersCount(state, getters) {
    return getters.pendingTransfers.length || 0
  },
  pendingTransfersByStoreId(state, getters, rootState, rootGetters) {
    return (storeId) => {
      console.log('Checking pending transfers for store:', storeId)

      // Get store region
      const storeRegion = rootGetters['global/storeRegionById'](storeId) || ''

      const pendingTransfers = getters.pendingTransfers.map((id) =>
        getters.transferById(id)
      )

      const filtered = pendingTransfers.filter((t) => {
        // Skip deleted or canceled transfers
        if (t.status === 'deleted' || t.status === 'cancelled') {
          return false
        }

        console.log(
          'Transfer:',
          t.id,
          'Status:',
          t.status,
          'Source:',
          t.sourceStoreId,
          'Dest:',
          t.destinationStoreId
        )

        // If store is directly involved, show it
        if (t.sourceStoreId === storeId || t.destinationStoreId === storeId) {
          return true
        }

        // If no destination store is set, check if in same region
        if (!t.destinationStoreId || t.destinationStoreId === '') {
          // Get transfer source store's region
          const transferSourceRegion = t.sourceStoreId
            ? rootGetters['global/storeRegionById'](t.sourceStoreId)
            : ''

          // If transfer has no source store or is in same region, show it
          return !t.sourceStoreId || transferSourceRegion === storeRegion
        }

        return false
      })

      console.log('Filtered transfers:', filtered)
      return filtered
    }
  },
  pendingTransfersCountByStoreId(state, getters) {
    return (storeId) => {
      const count = getters.pendingTransfersByStoreId(storeId).length || 0
      console.log('Pending count for store:', storeId, 'is:', count)
      return count
    }
  },
  closedTransfers(state, getters) {
    return getters.transfersOrdered
      .filter((t) => ['completed', 'rejected', 'cancelled'].includes(t.status))
      .map((t) => t.id)
  },
  transfersOrdered(state, getters) {
    return Object.values(getters.transfers).sort((a, b) => {
      // Convert Firestore timestamps to JS Date objects for comparison
      const dateA = (a.requestedAt && a.requestedAt.toDate()) || new Date(0)
      const dateB = (b.requestedAt && b.requestedAt.toDate()) || new Date(0)
      return dateB - dateA // Sort descending (newest first)
    })
  }
}

const actions = {
  bindStore: firestoreAction(({ bindFirestoreRef, commit, state }) => {
    commit('setRef', db.collection('transfers'))
    return bindFirestoreRef(
      'data',
      state.ref
        .where('status', 'in', [
          'requested',
          'approved',
          'completed',
          'inTransit'
        ])
        .where('date', '>=', DateTime.now().minus({ days: 30 }).toJSDate())
    )
  }),

  unbindStore: firestoreAction(({ unbindFirestoreRef }) => {
    unbindFirestoreRef('data')
  }),

  async updateItem({ getters }, { id, data }) {
    console.log('transfers/updateItem', id, data)
    return getters.ref.doc(id).set(data, { merge: true })
  },

  async createTransfer({ getters, rootGetters, dispatch }, data) {
    const id = newId()
    const now = Timestamp.now()

    const transferData = {
      id,
      transactionType: 'transfer',
      status: 'requested',
      date: data.date || now,

      itemType: data.itemType,
      itemId: data.itemId,
      itemName: data.itemName,
      sourceItemId: data.sourceItemId,
      destinationItemId: data.destinationItemId,

      quantity: data.quantity,
      unitType: data.unitType,
      unitCost: data.unitCost || 0,
      totalCost: data.totalCost || 0,

      notes: data.notes || '',

      sourceStoreId: data.sourceStoreId,
      sourceStoreName: data.sourceStoreName,
      destinationStoreId: data.destinationStoreId,
      destinationStoreName: data.destinationStoreName,

      createdAt: now,
      createdBy: rootGetters['user/id'],
      requestedAt: now,
      requestedBy: rootGetters['user/id']
    }

    return getters.ref.doc(id).set(transferData)
  },

  async approveTransfer(
    { rootGetters, dispatch, getters },
    { id, sourceStoreId }
  ) {
    console.log('Approving transfer:', id)
    const storeId = rootGetters['storeModule/id']
    const transfer = getters.transferById(id)
    if (!transfer) return null

    // Get source store if not already set
    const finalSourceStoreId =
      transfer.sourceStoreId || sourceStoreId || storeId
    if (!finalSourceStoreId) {
      console.error('No source store specified for transfer approval')
      return null
    }

    let unitCost = 0
    let sourceItemId = null

    // Calculate costs based on product type
    if (transfer.itemType === 'recipe') {
      unitCost = rootGetters['storeModule/recipeUnitCostById'](transfer.itemId)
    } else {
      sourceItemId = rootGetters['storeModule/localProductIdFromGenericId'](
        transfer.itemId,
        finalSourceStoreId
      )
      const sourcePrice =
        rootGetters['storeModule/productPriceByProductId'](sourceItemId)
      unitCost = sourcePrice ? sourcePrice.pricePerCountingUnit : 0
    }

    const result = await dispatch('updateItem', {
      id,
      data: {
        status: 'approved',
        sourceStoreId: finalSourceStoreId,
        sourceStoreName: rootGetters['global/stores'][finalSourceStoreId],
        sourceItemId,
        unitCost,
        totalCost: unitCost * transfer.quantity,
        approvedAt: Timestamp.now().toDate(),
        approvedBy: rootGetters['user/id']
      }
    })
    console.log('Transfer approved:', id)
    return result
  },

  async rejectTransfer({ rootGetters, dispatch }, { id, rejectedReason }) {
    return dispatch('updateItem', {
      id,
      data: {
        status: 'rejected',
        rejectedAt: Timestamp.now().toDate(),
        rejectedBy: rootGetters['user/id'],
        rejectedReason: rejectedReason || null
      }
    })
  },

  async cancelTransfer({ getters, rootGetters, dispatch }, { id }) {
    return dispatch('updateItem', {
      id,
      data: {
        status: 'cancelled',
        cancelledAt: Timestamp.now().toDate(),
        cancelledBy: rootGetters['user/id']
      }
    })
  },

  async inTransitTransfer({ dispatch }, id) {
    return dispatch('updateItem', {
      id,
      data: {
        status: 'inTransit',
        inTransitAt: Timestamp.now().toDate()
      }
    })
  },

  async completeTransfer({ getters, rootGetters, dispatch }, { id }) {
    return dispatch('updateItem', {
      id,
      data: {
        status: 'completed',
        completedAt: Timestamp.now().toDate(),
        completedBy: rootGetters['user/id']
      }
    })
  },

  async deleteTransfer({ getters, rootGetters }, { id }) {
    console.log('Deleting transfer:', id)
    const result = await getters.ref.doc(id).set(
      {
        status: 'deleted',
        deletedAt: Timestamp.now().toDate(),
        deletedBy: rootGetters['user/id']
      },
      { merge: true }
    )
    console.log('Transfer deleted:', id)
    return result
  },

  async updateTransfer({ getters, rootGetters }, { id, ...data }) {
    const updateData = {
      ...data,
      transactionType: 'transfer',
      updatedAt: Timestamp.now().toDate(),
      updatedBy: rootGetters['user/id']
    }
    return getters.ref.doc(id).set(updateData, { merge: true })
  }
}

const mutations = {
  setRef(state, ref) {
    state.ref = ref
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  }
}

const transfers = {
  namespaced: true,

  state,
  getters,
  mutations,
  actions
}

export default transfers

// transferId: {
//   id:
//   status:
//   notes:

//   productId:
//   productCategoryId:
//   quantity:
//   unit:
//   unitCost:

//   sourceStoreId:
//   destinationStoreId:

//   requestedAt:
//   requestedBy:
//   approvedAt:
//   approvedBy:
//   inTransitAt:
//   completedAt:
// }
